import { create } from "zustand"

import type { ProductInventoryTransactionsSearchParams } from "../schemas"

import {
  GetProductInventoryByTransactionDocument,
  type GetProductInventoryByTransactionQuery,
  type InventoryByTransactionItem,
} from "@/graphql/codegen/graphql"
import { createSearchStore } from "@/stores/createSearchStore"

type ProductInventoryTransactionsFilters = ProductInventoryTransactionsSearchParams & { productId: number }

const deduplicateTransactions = (
  newData: InventoryByTransactionItem[],
  existingData: InventoryByTransactionItem[]
): InventoryByTransactionItem[] => {
  const seen = new Set(existingData.map((item) => `${item.orderNumber}-${item.type}`))
  return [...existingData, ...newData.filter((item) => !seen.has(`${item.orderNumber}-${item.type}`))]
}

export const useProductInventoryTransactionsSearchStore = create(
  createSearchStore<
    InventoryByTransactionItem,
    ProductInventoryTransactionsFilters,
    GetProductInventoryByTransactionQuery
  >({
    documentNode: GetProductInventoryByTransactionDocument,
    deduplicateData: deduplicateTransactions,
    getQueryInput: (filters: ProductInventoryTransactionsFilters, offset: number, limit: number) => {
      const { field, order, query, productId, warehouseAreaId } = filters

      return {
        offset,
        limit,
        sort: field && order ? { field, order } : undefined,
        filter: {
          query,
          productId,
          warehouseId: warehouseAreaId,
        },
      }
    },
    getQueryVariables: (queryInput) => ({ input: queryInput }),
    extractDataFromResponse: (data) => ({
      __typename: data.inventory.listByTransaction.__typename,
      data:
        data.inventory.listByTransaction.__typename === "InventoryByTransactionListSuccess"
          ? (data.inventory.listByTransaction.items as InventoryByTransactionItem[])
          : [],
      error:
        data.inventory.listByTransaction.__typename === "InventoryByTransactionListFailure"
          ? data.inventory.listByTransaction.error
          : undefined,
    }),
    hasMoreData: (response, state) => response.data.length === state.limit,
  })
)
